import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../../api/endpoints.js';
import {
  Button, Card, Dialog, ErrorState, Field, Input, Loading,
} from '../../components/ui.jsx';
import { toast } from '../../lib/toast.js';
import { useAuth } from '../auth/AuthContext.jsx';
import { PERMISSIONS } from '../../lib/permissions.js';
import { minorFromRupees, money, number, rupeesFromMinor } from '../../lib/format.js';

/**
 * What each feature costs, in credits, grouped by the app that charges it.
 *
 * These are the numbers the apps read at runtime — nothing is priced in the
 * app bundles, so an ad costs whatever this screen last said it costs, from
 * the next request onwards. That is also why there is a confirmation step:
 * saving changes what the next person to post an ad is charged.
 *
 * The split is by who pays, because that is the question somebody setting a
 * price is actually asking. A few knobs genuinely belong to both apps and are
 * kept apart rather than filed under one of them — a boost price that quietly
 * changed for owners because somebody was tuning agencies would be a bug
 * caused by a heading.
 *
 * Listing ads are priced by plan rather than by app: one list of lengths, each
 * with an owner/tenant-app price and a broker-app price. That list is the only
 * price for an ad — publishing, resuming and renewing all charge the plan the
 * lister picks.
 */

/** Owners and tenants, in the rental app. */
const RENTAL_CREDITS = [
  {
    key: 'ownerContactCredits',
    label: 'Seeing who to call',
    hint: "A tenant unlocking a listing's contact. Charged once per listing, for good.",
  },
  {
    key: 'signupGrant',
    label: 'Signup grant',
    hint: 'Given once, when a tenant or owner account is created. Not a charge.',
  },
];

/** Agencies, in the broker app. */
const BROKER_CREDITS = [
  {
    key: 'enquiryUnlockCredits',
    label: "Unlocking an owner's enquiry",
    hint: 'Seeing who to call about an enquiry an owner sent them. Charged once.',
  },
  {
    key: 'featuredProfileCredits',
    label: 'Featured profile to owners',
    hint: 'Puts the agency in the featured list at the top of the directory owners hire from.',
  },
  {
    key: 'signupGrantBroker',
    label: 'Signup grant',
    hint: 'Given once, when a broker account is created. They spend theirs on leads.',
  },
];

const BROKER_PERIODS = [
  {
    key: 'featuredProfileDays',
    label: 'Featured runs for',
    hint: 'How long one featured charge buys. Buying again extends the run.',
  },
];

/**
 * Knobs both apps read.
 *
 * One boost price and period cover both apps, and so do the renewal reminder
 * and the low-balance warning. None can be set per app without a schema
 * change, so none is shown as though it could.
 */
const SHARED_CREDITS = [
  {
    key: 'boostCredits',
    label: 'Boosting a listing',
    hint: 'Lifts one listing above the rest in tenant search, on top of the ad. Owners and agencies pay the same.',
  },
  {
    key: 'lowBalanceThreshold',
    label: 'Warn about a low balance at',
    hint: 'When someone has this many credits or fewer, the apps tell them they are running low. 0 warns only when they run out.',
    max: 100000,
    fallback: 10,
  },
];

const SHARED_PERIODS = [
  { key: 'boostDays', label: 'A boost runs for', hint: 'How long one boost charge buys, in both apps.' },
];

/** Shown with the ad plans, because it is about when an ad renews. */
const AD_PERIODS = [
  {
    key: 'renewalNoticeDays',
    label: 'Ask about renewing',
    hint: 'How many days before an ad ends the lister is asked whether to renew it. Nothing renews or is charged without them saying yes.',
    max: 30,
    fallback: 3,
  },
];

const CREDIT_FIELDS = [...RENTAL_CREDITS, ...BROKER_CREDITS, ...SHARED_CREDITS];
const PERIOD_FIELDS = [...BROKER_PERIODS, ...SHARED_PERIODS, ...AD_PERIODS];
const WALLET_FIELDS = [...CREDIT_FIELDS, ...PERIOD_FIELDS];

/** Limits from the config contract; the API enforces the same ones. */
const MAX_PLANS = 6;
const MAX_PLAN_DAYS = 365;
const MAX_PLAN_CREDITS = 100000;
const MAX_BANDS = 10;

/*
 * Blank is not zero.
 *
 * These are text boxes, so an emptied field arrives as '' — and `Number('')`
 * is 0, which would have saved "ads are free" without anybody typing a
 * number. Anything that is not a whole number is a validation error, and
 * clearing a field is one of those things.
 */
const isWholeNumber = (value) => {
  if (value == null || String(value).trim() === '') return false;
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed >= 0;
};

/** The server's value for a wallet field, or the default it applies when unset. */
const serverWallet = (server, entry) => server.wallet?.[entry.key] ?? entry.fallback;

/**
 * The ad plans currently in force.
 *
 * GET /config always returns the effective list. The fallback is only for a
 * backend that predates plans, and builds the single plan it was charging.
 */
function serverPlans(server) {
  const wallet = server.wallet || {};
  if (Array.isArray(wallet.adDurations) && wallet.adDurations.length) return wallet.adDurations;
  return [{
    days: wallet.adPeriodDays ?? 30,
    ownerCredits: wallet.adPostCredits ?? 0,
    brokerCredits: wallet.adPostCreditsBroker ?? 0,
  }];
}

/** Plans in a comparable order, as numbers. */
const normalisePlans = (plans) =>
  plans
    .map((plan) => ({
      days: Number(plan.days),
      ownerCredits: Number(plan.ownerCredits),
      brokerCredits: Number(plan.brokerCredits),
    }))
    .sort((a, b) => a.days - b.days);

const FREE_DEFAULTS = { enabled: false, days: 90, maxPerLister: null, startsAt: null, endsAt: null };
const AUDIENCES = [
  { key: 'owners', label: 'Owners', app: 'rental app' },
  { key: 'brokers', label: 'Brokers', app: 'broker app' },
];

/** An ISO timestamp as the yyyy-mm-dd a date input shows (local day). */
const toDateInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/** Offer window edges: it starts at the start of its first day and ends at the end of its last. */
const fromDateInput = (value, endOfDay) => {
  if (!value) return null;
  const d = new Date(`${value}T${endOfDay ? '23:59:59' : '00:00:00'}`);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
};

/** The two free-listing offers in their editable (string) form. */
const freeDraftFrom = (config) =>
  Object.fromEntries(AUDIENCES.map(({ key }) => {
    const offer = { ...FREE_DEFAULTS, ...(config.wallet?.freeListing?.[key] || {}) };
    return [key, {
      enabled: Boolean(offer.enabled),
      days: String(offer.days),
      maxPerLister: offer.maxPerLister == null ? '' : String(offer.maxPerLister),
      startsAt: toDateInput(offer.startsAt),
      endsAt: toDateInput(offer.endsAt),
    }];
  }));

/** The editable form back to what the API stores. */
const freeToPayload = (free) =>
  Object.fromEntries(AUDIENCES.map(({ key }) => [key, {
    enabled: free[key].enabled,
    days: Number(free[key].days),
    maxPerLister: String(free[key].maxPerLister).trim() === '' ? null : Number(free[key].maxPerLister),
    startsAt: fromDateInput(free[key].startsAt, false),
    endsAt: fromDateInput(free[key].endsAt, true),
  }]));

/** One line describing an offer, for the confirmation. */
const describeFree = (offer) => {
  if (!offer.enabled) return 'off';
  const cap = offer.maxPerLister == null ? 'no limit per lister' : `up to ${offer.maxPerLister} per lister`;
  const window = [
    offer.startsAt ? `from ${toDateInput(offer.startsAt)}` : null,
    offer.endsAt ? `until ${toDateInput(offer.endsAt)}` : null,
  ].filter(Boolean).join(' ') || 'no end date';
  return `free for ${offer.days} days, ${cap}, ${window}`;
};

/** The editable shape of the bits of platform config this screen owns. */
function draftFrom(config) {
  const wallet = { ...config.wallet };
  WALLET_FIELDS.forEach((entry) => {
    if (wallet[entry.key] == null && entry.fallback != null) wallet[entry.key] = entry.fallback;
  });
  const plans = serverPlans(config).map((plan) => ({ ...plan }));
  const defaultIndex = Math.max(0, plans.findIndex((plan) => Number(plan.days) === Number(wallet.adPeriodDays)));

  return {
    wallet,
    plans,
    defaultIndex,
    free: freeDraftFrom(config),
    leadCredits: config.broker?.defaultLeadCreditCost,
    bands: (config.broker?.leadCreditCostByBudgetBand || []).map((band) => ({ ...band })),
  };
}

/**
 * Credit rates.
 *
 * Everything on this screen is one `PATCH /admin/config` away from being live
 * in both apps, so the form holds a local draft and only sends what actually
 * changed. Sending the whole config back would overwrite a section somebody
 * else edited while this tab was open.
 */
export default function SettingsTab() {
  const { can } = useAuth();
  const canManage = can(PERMISSIONS.CONFIG_MANAGE);

  const queryClient = useQueryClient();
  const config = useQuery({ queryKey: ['admin-platform-config'], queryFn: api.config.get });

  /*
   * `null` means untouched, and an untouched form is derived from the server
   * on every render rather than copied into state. That is what makes a
   * refetch show the current rates without an effect racing the query to
   * seed them — the first keystroke is what takes a local copy.
   */
  const [edits, setEdits] = useState(null);
  const [errors, setErrors] = useState({});
  const [confirming, setConfirming] = useState(false);

  const save = useMutation({
    mutationFn: (patch) => api.config.update(patch),
    onSuccess: () => {
      toast.success('Credit rates saved. Both apps pick them up on their next request.');
      queryClient.invalidateQueries({ queryKey: ['admin-platform-config'] });
      setConfirming(false);
      setEdits(null);
    },
    onError: (error) => toast.error(error.message),
  });

  if (config.isLoading) {
    return (
      <Card>
        <Loading label="Loading credit rates" />
      </Card>
    );
  }

  if (config.isError) {
    return (
      <Card>
        <ErrorState error={config.error} onRetry={config.refetch} />
      </Card>
    );
  }

  const server = config.data;
  const draft = edits ?? draftFrom(server);

  const update = (change) => setEdits((current) => change(current ?? draftFrom(server)));

  const setWallet = (key, value) => {
    update((current) => ({ ...current, wallet: { ...current.wallet, [key]: value } }));
    setErrors((current) => ({ ...current, [key]: null }));
  };


  /** Edits to one row of a list invalidate that row's message, not the rest. */
  const clearError = (key) => setErrors((current) => ({ ...current, [key]: null }));

  /*
   * Adding or removing a row shifts every index after it, so a message keyed
   * `plan-2-days` would end up under the wrong row. Clearing them is simpler
   * than renumbering, and the next review finds them again.
   */
  const clearRowErrors = (prefix) =>
    setErrors((current) =>
      Object.fromEntries(Object.entries(current).filter(([key]) => !key.startsWith(prefix))));

  const setPlan = (index, key, value) => {
    update((current) => ({
      ...current,
      plans: current.plans.map((plan, i) => (i === index ? { ...plan, [key]: value } : plan)),
    }));
    clearError(`plan-${index}-${key}`);
    clearError('plans');
  };

  const addPlan = () => {
    update((current) => ({
      ...current,
      plans: [...current.plans, { days: '', ownerCredits: '', brokerCredits: '' }],
    }));
    clearRowErrors('plan');
  };

  const removePlan = (index) => {
    update((current) => {
      // The default follows its row; removing the default row makes the first
      // remaining plan the default, which the confirmation then spells out.
      let { defaultIndex } = current;
      if (index === defaultIndex) defaultIndex = 0;
      else if (index < defaultIndex) defaultIndex -= 1;
      return { ...current, plans: current.plans.filter((_, i) => i !== index), defaultIndex };
    });
    clearRowErrors('plan');
  };

  const setFree = (audience, key, value) => {
    update((current) => ({
      ...current,
      free: { ...current.free, [audience]: { ...current.free[audience], [key]: value } },
    }));
    setErrors((current) => ({
      ...current,
      [`free-${audience}-days`]: null,
      [`free-${audience}-max`]: null,
      [`free-${audience}-end`]: null,
    }));
  };

  const setDefaultPlan = (index) => update((current) => ({ ...current, defaultIndex: index }));

  const setBand = (index, key, value) => {
    update((current) => ({
      ...current,
      bands: current.bands.map((band, i) => (i === index ? { ...band, [key]: value } : band)),
    }));
    clearError(`band-${index}-${key}`);
  };

  const addBand = () => {
    update((current) => ({
      ...current,
      bands: [...current.bands, { maxBudgetMinor: null, credits: '' }],
    }));
    clearRowErrors('band');
  };

  const removeBand = (index) => {
    update((current) => ({ ...current, bands: current.bands.filter((_, i) => i !== index) }));
    clearRowErrors('band');
  };

  /** Only what differs from the server, so a save cannot carry stale siblings. */
  const changes = () => {
    const wallet = {};
    WALLET_FIELDS.forEach((entry) => {
      const next = Number(draft.wallet[entry.key]);
      if (next !== Number(serverWallet(server, entry))) wallet[entry.key] = next;
    });

    /*
     * Plans are compared as a set ordered by length, and sent the same way: a
     * row typed out of order is the same price list, not a change.
     */
    const nextPlans = normalisePlans(draft.plans);
    const currentPlans = normalisePlans(serverPlans(server));
    const plansChanged =
      nextPlans.length !== currentPlans.length ||
      nextPlans.some(
        (plan, i) =>
          plan.days !== currentPlans[i].days ||
          plan.ownerCredits !== currentPlans[i].ownerCredits ||
          plan.brokerCredits !== currentPlans[i].brokerCredits,
      );
    if (plansChanged) wallet.adDurations = nextPlans;

    const defaultDays = Number(draft.plans[draft.defaultIndex]?.days);
    if (defaultDays !== Number(server.wallet?.adPeriodDays)) wallet.adPeriodDays = defaultDays;

    const nextFree = freeToPayload(draft.free);
    const currentFree = freeToPayload(freeDraftFrom(server));
    if (JSON.stringify(nextFree) !== JSON.stringify(currentFree)) wallet.freeListing = nextFree;

    const broker = {};
    if (Number(draft.leadCredits) !== Number(server.broker?.defaultLeadCreditCost)) {
      broker.defaultLeadCreditCost = Number(draft.leadCredits);
    }

    const serverBands = server.broker?.leadCreditCostByBudgetBand || [];
    const bandsChanged =
      draft.bands.length !== serverBands.length ||
      draft.bands.some(
        (band, i) =>
          Number(band.maxBudgetMinor) !== Number(serverBands[i]?.maxBudgetMinor) ||
          Number(band.credits) !== Number(serverBands[i]?.credits),
      );
    if (bandsChanged) {
      broker.leadCreditCostByBudgetBand = draft.bands.map((band) => ({
        maxBudgetMinor: Number(band.maxBudgetMinor),
        credits: Number(band.credits),
      }));
    }

    return {
      ...(Object.keys(wallet).length ? { wallet } : {}),
      ...(Object.keys(broker).length ? { broker } : {}),
    };
  };

  const patch = changes();
  const changedCount =
    Object.keys(patch.wallet || {}).length + Object.keys(patch.broker || {}).length;

  const validate = () => {
    const found = {};
    CREDIT_FIELDS.forEach(({ key, label, max }) => {
      const value = draft.wallet[key];
      if (!isWholeNumber(value)) found[key] = `${label} must be a whole number of credits.`;
      else if (max != null && Number(value) > max) found[key] = `${label} can be at most ${number(max)}.`;
    });
    PERIOD_FIELDS.forEach(({ key, label, max }) => {
      const value = draft.wallet[key];
      if (!isWholeNumber(value) || Number(value) < 1) found[key] = `${label} must be at least one day.`;
      else if (max != null && Number(value) > max) found[key] = `${label} can be at most ${max} days.`;
    });

    AUDIENCES.forEach(({ key, label }) => {
      const offer = draft.free[key];
      if (!isWholeNumber(offer.days) || Number(offer.days) < 1 || Number(offer.days) > MAX_PLAN_DAYS) {
        found[`free-${key}-days`] = `1 to ${MAX_PLAN_DAYS} days.`;
      }
      if (String(offer.maxPerLister).trim() !== '' && (!isWholeNumber(offer.maxPerLister) || Number(offer.maxPerLister) < 1)) {
        found[`free-${key}-max`] = 'A whole number of at least 1, or leave empty for no limit.';
      }
      if (offer.startsAt && offer.endsAt && offer.endsAt < offer.startsAt) {
        found[`free-${key}-end`] = `${label}: the offer must end on or after the day it starts.`;
      }
    });

    if (draft.plans.length < 1) found.plans = 'Keep at least one ad plan.';
    if (draft.plans.length > MAX_PLANS) found.plans = `At most ${MAX_PLANS} ad plans.`;
    const seenDays = new Set();
    draft.plans.forEach((plan, i) => {
      const days = Number(plan.days);
      if (!isWholeNumber(plan.days) || days < 1 || days > MAX_PLAN_DAYS) {
        found[`plan-${i}-days`] = `1 to ${MAX_PLAN_DAYS} days.`;
      } else if (seenDays.has(days)) {
        found[`plan-${i}-days`] = 'Another plan already has this length.';
      }
      seenDays.add(days);
      ['ownerCredits', 'brokerCredits'].forEach((key) => {
        if (!isWholeNumber(plan[key]) || Number(plan[key]) > MAX_PLAN_CREDITS) {
          found[`plan-${i}-${key}`] = `Whole credits, 0 to ${number(MAX_PLAN_CREDITS)}.`;
        }
      });
    });

    if (!isWholeNumber(draft.leadCredits) || Number(draft.leadCredits) < 1) {
      found.leadCredits = 'Lead cost must be a whole number of at least 1 credit.';
    }
    if (draft.bands.length > MAX_BANDS) found.bands = `At most ${MAX_BANDS} bands.`;
    draft.bands.forEach((band, i) => {
      const budget = band.maxBudgetMinor;
      const previous = i > 0 ? draft.bands[i - 1].maxBudgetMinor : null;
      if (budget == null || budget === '' || !Number.isInteger(Number(budget)) || Number(budget) <= 0) {
        found[`band-${i}-maxBudgetMinor`] = 'Enter a budget above ₹0.';
      } else if (previous != null && previous !== '' && Number(budget) <= Number(previous)) {
        found[`band-${i}-maxBudgetMinor`] = `Has to be more than the band above (${money(previous)}).`;
      }
      if (!isWholeNumber(band.credits) || Number(band.credits) < 1) {
        found[`band-${i}-credits`] = 'At least 1 whole credit.';
      }
    });

    setErrors(found);
    return Object.keys(found).length === 0;
  };

  const review = () => {
    if (!validate()) {
      toast.error('Some rates need fixing before they can be saved.');
      return;
    }
    if (!changedCount) {
      toast.success('Nothing to save — these are the rates already in force.');
      return;
    }
    setConfirming(true);
  };

  const discard = () => {
    setEdits(null);
    setErrors({});
  };

  /** One input, wired to the draft. Every section is built from these. */
  const rateField = (entry, { suffix = '' } = {}) => (
    <Field
      key={entry.key}
      label={suffix ? `${entry.label} (${suffix})` : entry.label}
      hint={entry.hint}
      error={errors[entry.key]}
      id={`rate-${entry.key}`}
    >
      <Input
        id={`rate-${entry.key}`}
        type="text"
        inputMode="numeric"
        disabled={!canManage}
        error={errors[entry.key]}
        value={draft.wallet[entry.key] ?? ''}
        onChange={(event) => setWallet(entry.key, event.target.value)}
      />
    </Field>
  );

  /** A bare input in a table cell; the column header is its label. */
  const cellInput = ({ id, label, value, error, onChange }) => (
    <Field error={error} id={id}>
      <Input
        id={id}
        aria-label={label}
        type="text"
        inputMode="numeric"
        disabled={!canManage}
        error={error}
        value={value ?? ''}
        onChange={(event) => onChange(event.target.value)}
      />
    </Field>
  );

  return (
    <>
      <div className="stack">
        <Card
          title="Credit rates"
          subtitle="Read by the apps at runtime. Nothing here is baked into a release, so a change takes effect on the next request — and on everybody at once."
          actions={
            canManage ? (
              <div className="row">
                <Button variant="ghost" onClick={discard} disabled={!changedCount}>
                  Discard
                </Button>
                <Button variant="primary" onClick={review} disabled={!changedCount}>
                  {changedCount ? `Review ${changedCount} change${changedCount === 1 ? '' : 's'}` : 'Save'}
                </Button>
              </div>
            ) : null
          }
          bodyless
        />

        <Card
          title="Free listing offer"
          subtitle="An alternative to the credit price list below, set separately for owners and brokers. While an offer is on, that group's new ads and renewals cost nothing and offer only the free length. Turning it off, or the end date passing, never shortens an ad already running: its next renewal is simply charged at the ad plan price."
        >
          <div className="stack">
            {AUDIENCES.map(({ key, label, app }) => {
              const offer = draft.free[key];
              return (
                <Card key={key} title={label} subtitle={`Applies in the ${app}.`}>
                  <div className="stack">
                    <label className="row">
                      <input
                        type="checkbox"
                        checked={offer.enabled}
                        disabled={!canManage}
                        onChange={(event) => setFree(key, 'enabled', event.target.checked)}
                      />
                      <span>{`List ads for free for ${label.toLowerCase()}`}</span>
                    </label>

                    <div className="field-row">
                      <Field label="Each ad runs for (days)" hint="Counted from the day it goes up." error={errors[`free-${key}-days`]} id={`free-${key}-days`}>
                        <Input
                          id={`free-${key}-days`}
                          type="text"
                          inputMode="numeric"
                          disabled={!canManage}
                          error={errors[`free-${key}-days`]}
                          value={offer.days}
                          onChange={(event) => setFree(key, 'days', event.target.value)}
                        />
                      </Field>
                      <Field
                        label="Free listings per lister"
                        hint="Live at once. Empty means no limit. Beyond it, the ad plans below apply."
                        error={errors[`free-${key}-max`]}
                        id={`free-${key}-max`}
                      >
                        <Input
                          id={`free-${key}-max`}
                          type="text"
                          inputMode="numeric"
                          disabled={!canManage}
                          error={errors[`free-${key}-max`]}
                          value={offer.maxPerLister}
                          onChange={(event) => setFree(key, 'maxPerLister', event.target.value)}
                        />
                      </Field>
                    </div>

                    <div className="field-row">
                      <Field label="Starts on" hint="Empty means as soon as it is saved." id={`free-${key}-starts`}>
                        <Input
                          id={`free-${key}-starts`}
                          type="date"
                          disabled={!canManage}
                          value={offer.startsAt}
                          onChange={(event) => setFree(key, 'startsAt', event.target.value)}
                        />
                      </Field>
                      <Field label="Last day" hint="Empty means it runs until you turn it off." error={errors[`free-${key}-end`]} id={`free-${key}-ends`}>
                        <Input
                          id={`free-${key}-ends`}
                          type="date"
                          disabled={!canManage}
                          error={errors[`free-${key}-end`]}
                          value={offer.endsAt}
                          onChange={(event) => setFree(key, 'endsAt', event.target.value)}
                        />
                      </Field>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </Card>

        <Card
          title="Ad plans"
          subtitle="What a listing ad costs, by how long it runs. This is the one price list for ads in both apps: publishing, resuming and renewing all charge the plan the lister picks. A renewal charges the chosen plan's price, and the lister is asked before anything renews."
          actions={
            canManage ? (
              <Button variant="outline" size="sm" onClick={addPlan} disabled={draft.plans.length >= MAX_PLANS}>
                Add plan
              </Button>
            ) : null
          }
        >
          <div className="stack">
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Default</th>
                    <th>Runs for (days)</th>
                    <th>Owner/tenant app (credits)</th>
                    <th>Broker app (credits)</th>
                    {canManage ? <th aria-label="Actions" /> : null}
                  </tr>
                </thead>
                <tbody>
                  {draft.plans.map((plan, index) => (
                    // Keyed by position for the same reason as the lead bands
                    // below: keying on the days remounts the row mid-typing.
                    // eslint-disable-next-line react/no-array-index-key
                    <tr key={index}>
                      <td>
                        <input
                          type="radio"
                          name="default-ad-plan"
                          aria-label={`Make ${plan.days || 'this'}-day plan the default`}
                          checked={draft.defaultIndex === index}
                          disabled={!canManage}
                          onChange={() => setDefaultPlan(index)}
                        />
                      </td>
                      <td>
                        {cellInput({
                          id: `plan-days-${index}`,
                          label: `Plan ${index + 1}: days`,
                          value: plan.days,
                          error: errors[`plan-${index}-days`],
                          onChange: (value) => setPlan(index, 'days', value),
                        })}
                      </td>
                      <td>
                        {cellInput({
                          id: `plan-owner-${index}`,
                          label: `Plan ${index + 1}: owner/tenant app price`,
                          value: plan.ownerCredits,
                          error: errors[`plan-${index}-ownerCredits`],
                          onChange: (value) => setPlan(index, 'ownerCredits', value),
                        })}
                      </td>
                      <td>
                        {cellInput({
                          id: `plan-broker-${index}`,
                          label: `Plan ${index + 1}: broker app price`,
                          value: plan.brokerCredits,
                          error: errors[`plan-${index}-brokerCredits`],
                          onChange: (value) => setPlan(index, 'brokerCredits', value),
                        })}
                      </td>
                      {canManage ? (
                        <td className="table__actions">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removePlan(index)}
                            disabled={draft.plans.length <= 1}
                          >
                            Remove
                          </Button>
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {errors.plans ? (
              <span className="field__error" role="alert">{errors.plans}</span>
            ) : (
              <span className="field__hint">
                {`The default plan is the one the apps offer first. Between 1 and ${MAX_PLANS} plans, each a different length.`}
              </span>
            )}

            <div className="field-row">
              {AD_PERIODS.map((entry) => rateField(entry, { suffix: 'days before it ends' }))}
            </div>
          </div>
        </Card>

        <Card title="Rental app" subtitle="What owners and tenants are charged, besides ads.">
          <div className="stack">{RENTAL_CREDITS.map((entry) => rateField(entry))}</div>
        </Card>

        <Card title="Broker app" subtitle="What agencies are charged, besides ads.">
          <div className="stack">
            {BROKER_CREDITS.map((entry) => rateField(entry))}

            <div className="field-row">
              {BROKER_PERIODS.map((entry) => rateField(entry, { suffix: 'days' }))}
            </div>

            {/* Leads are broker-only, so their pricing belongs in this
                section rather than in a table of its own. */}
            <Card
              title="What a lead costs"
              subtitle="Priced by the tenant's budget, lowest band first; the default applies above the last band."
              actions={
                canManage ? (
                  <Button variant="outline" size="sm" onClick={addBand} disabled={draft.bands.length >= MAX_BANDS}>
                    Add band
                  </Button>
                ) : null
              }
            >
              <div className="stack">
                {draft.bands.map((band, index) => (
                  /*
                   * Keyed by position, not by value: keying on the budget
                   * meant every keystroke changed the key, and React
                   * remounted the row and took the focus with it.
                   */
                  // eslint-disable-next-line react/no-array-index-key
                  <div className="field-row" key={index}>
                    <Field
                      label="Budget up to (₹)"
                      hint="A tenant's maximum monthly rent. Each band has to be higher than the one above it."
                      error={errors[`band-${index}-maxBudgetMinor`]}
                      id={`band-budget-${index}`}
                    >
                      <Input
                        id={`band-budget-${index}`}
                        type="text"
                        inputMode="numeric"
                        disabled={!canManage}
                        error={errors[`band-${index}-maxBudgetMinor`]}
                        value={rupeesFromMinor(band.maxBudgetMinor)}
                        onChange={(event) => setBand(index, 'maxBudgetMinor', minorFromRupees(event.target.value))}
                      />
                    </Field>
                    <Field
                      label="Costs"
                      hint="Credits per lead in this band."
                      error={errors[`band-${index}-credits`]}
                      id={`band-credits-${index}`}
                    >
                      <Input
                        id={`band-credits-${index}`}
                        type="text"
                        inputMode="numeric"
                        disabled={!canManage}
                        error={errors[`band-${index}-credits`]}
                        value={band.credits ?? ''}
                        onChange={(event) => setBand(index, 'credits', event.target.value)}
                      />
                    </Field>
                    {canManage ? (
                      <div className="row">
                        <Button variant="ghost" size="sm" onClick={() => removeBand(index)}>
                          Remove band
                        </Button>
                      </div>
                    ) : null}
                  </div>
                ))}

                {errors.bands ? <span className="field__error" role="alert">{errors.bands}</span> : null}
                {!draft.bands.length ? (
                  <span className="field__hint">No bands: every lead costs the default below.</span>
                ) : null}

                <Field
                  label="Everything above that"
                  hint="The default lead cost, used when no band matches. At least 1 credit."
                  error={errors.leadCredits}
                  id="rate-lead-default"
                >
                  <Input
                    id="rate-lead-default"
                    type="text"
                    inputMode="numeric"
                    disabled={!canManage}
                    error={errors.leadCredits}
                    value={draft.leadCredits ?? ''}
                    onChange={(event) => {
                      const { value } = event.target;
                      update((current) => ({ ...current, leadCredits: value }));
                      clearError('leadCredits');
                    }}
                  />
                </Field>
              </div>
            </Card>
          </div>
        </Card>

        <Card
          title="Both apps"
          subtitle="Shared by owners and agencies. Changing one moves both apps."
        >
          <div className="stack">
            {SHARED_CREDITS.map((entry) => rateField(entry))}
            {SHARED_PERIODS.map((entry) => rateField(entry, { suffix: 'days' }))}
          </div>
        </Card>
      </div>

      <Dialog
        open={confirming}
        onClose={() => setConfirming(false)}
        title="Change what people are charged?"
        warning="This is live for everyone as soon as it saves. Credits already spent are not revisited — this is what the next ad, renewal, boost or lead costs. Recorded in the audit log against your account."
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirming(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={() => save.mutate(patch)} loading={save.isPending}>
              Save rates
            </Button>
          </>
        }
      >
        <ChangeList patch={patch} server={server} />
      </Dialog>
    </>
  );
}

/** "30/60": owner/tenant-app price, then broker-app price. */
const planPrice = (plan) => `${number(plan.ownerCredits)}/${number(plan.brokerCredits)}`;

/**
 * Plan rows, matched by length.
 *
 * A plan's days are its identity as far as a reader is concerned, so a
 * changed length reads as one plan removed and another added — which is what
 * it is to anybody choosing between them.
 */
function planRows(next, current) {
  const rows = [];
  next.forEach((plan) => {
    const before = current.find((entry) => entry.days === plan.days);
    if (!before) {
      rows.push(`Added ${plan.days}-day plan at ${planPrice(plan)} credits`);
    } else if (before.ownerCredits !== plan.ownerCredits || before.brokerCredits !== plan.brokerCredits) {
      rows.push(`${plan.days} days: ${planPrice(before)} → ${planPrice(plan)} credits`);
    }
  });
  current.forEach((plan) => {
    if (!next.some((entry) => entry.days === plan.days)) {
      rows.push(`Removed ${plan.days}-day plan (was ${planPrice(plan)} credits)`);
    }
  });
  return rows;
}

/** Lead bands, matched by their budget ceiling. */
function bandRows(next, current) {
  const rows = [];
  next.forEach((band) => {
    const before = current.find((entry) => Number(entry.maxBudgetMinor) === band.maxBudgetMinor);
    if (!before) {
      rows.push(`Added band ≤ ${money(band.maxBudgetMinor)} at ${number(band.credits)} credits`);
    } else if (Number(before.credits) !== band.credits) {
      rows.push(`Lead up to ${money(band.maxBudgetMinor)}: ${number(before.credits)} → ${number(band.credits)} credits`);
    }
  });
  current.forEach((band) => {
    if (!next.some((entry) => entry.maxBudgetMinor === Number(band.maxBudgetMinor))) {
      rows.push(`Removed band ≤ ${money(band.maxBudgetMinor)} (was ${number(band.credits)} credits)`);
    }
  });
  return rows;
}

/** Old value to new, for every field that actually moved. */
function ChangeList({ patch, server }) {
  const labelOf = (key) => WALLET_FIELDS.find((entry) => entry.key === key)?.label || key;

  /*
   * The app is part of the label here. Both apps have a "Signup grant", so a
   * confirmation reading "Signup grant: 50 → 80" would not say whose number
   * just moved.
   */
  const appOf = (key) => {
    if (RENTAL_CREDITS.some((entry) => entry.key === key)) return 'Rental app';
    if ([...BROKER_CREDITS, ...BROKER_PERIODS].some((entry) => entry.key === key)) return 'Broker app';
    if (AD_PERIODS.some((entry) => entry.key === key)) return 'Ad plans';
    return 'Both apps';
  };

  const unitOf = (key) => {
    if (key === 'renewalNoticeDays') return 'days before it ends';
    if (key.endsWith('Days')) return 'days';
    if (key === 'lowBalanceThreshold') return 'credits or fewer';
    return 'credits';
  };

  const { adDurations, adPeriodDays, freeListing, ...scalars } = patch.wallet || {};

  const scalarRows = Object.entries(scalars).map(([key, value]) => {
    const entry = WALLET_FIELDS.find((field) => field.key === key);
    const from = entry ? serverWallet(server, entry) : server.wallet?.[key];
    return {
      label: `${appOf(key)} · ${labelOf(key)}`,
      text: `: ${from == null ? '—' : number(from)} → ${number(value)} ${unitOf(key)}`,
    };
  });

  const listRows = [
    ...(freeListing
      ? AUDIENCES
        .filter(({ key }) => JSON.stringify(freeListing[key]) !== JSON.stringify(freeToPayload(freeDraftFrom(server))[key]))
        .map(({ key, label }) => ({
          label: `Free listing offer · ${label}`,
          text: `: ${describeFree({ ...FREE_DEFAULTS, ...(server.wallet?.freeListing?.[key] || {}) })} → ${describeFree(freeListing[key])}`,
        }))
      : []),
    ...(adDurations
      ? planRows(adDurations, normalisePlans(serverPlans(server))).map((text) => ({
        label: 'Ad plans (owner/tenant app / broker app)',
        text: `: ${text}`,
      }))
      : []),
    ...(adPeriodDays != null
      ? [{
        label: 'Ad plans · Default plan',
        text: `: ${server.wallet?.adPeriodDays == null ? '—' : `${number(server.wallet.adPeriodDays)} days`} → ${number(adPeriodDays)} days`,
      }]
      : []),
    ...(patch.broker?.defaultLeadCreditCost != null
      ? [{
        label: 'Broker app · Lead, default',
        text: `: ${server.broker?.defaultLeadCreditCost == null ? '—' : number(server.broker.defaultLeadCreditCost)} → ${number(patch.broker.defaultLeadCreditCost)} credits`,
      }]
      : []),
    ...(patch.broker?.leadCreditCostByBudgetBand
      ? bandRows(patch.broker.leadCreditCostByBudgetBand, server.broker?.leadCreditCostByBudgetBand || [])
        .map((text) => ({ label: 'Broker app · Lead bands', text: `: ${text}` }))
      : []),
  ];

  const rows = [...scalarRows, ...listRows];

  return (
    <ul className="stack stack--sm">
      {rows.map((row, index) => (
        // Several rows share a label (one per plan or band), so position is the key.
        // eslint-disable-next-line react/no-array-index-key
        <li key={index}>
          <strong>{row.label}</strong>
          {row.text}
        </li>
      ))}
    </ul>
  );
}
